import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import RequireAuth from './components/RequireAuth';
// Build 23 (Daniel 2026-09-18): staff who click a lender portal link get the
// lead instead of a silent bounce to the dashboard.
import StaffToLead from './components/StaffToLead';
import Layout from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';

/*
 * B-9 (Daniel 2026-08-17): route-level code splitting.
 *
 * Before this, every page was a static import, so the app shipped as one
 * 2.8 MB JS chunk (700 KB gzipped). Every visitor paid for all 100+ routes
 * on first paint, and Pages deploys crawled from 1 minute to 8-10 uploading
 * one giant file plus its 9.7 MB sourcemap on every release.
 *
 * The pages below stay EAGER on purpose. They are the first-paint path:
 * login and the password recovery pages (what a logged-out user sees first)
 * and Home (what a logged-in user lands on). Making these lazy would add a
 * loading flash to the two screens where speed is actually felt.
 *
 * RequireAuth, Layout and ErrorBoundary stay eager too. They are wrappers
 * on nearly every route, so lazy-loading them saves nothing, and the error
 * boundary must already be in memory when a lazy page fails to load.
 */
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Home from './pages/Home';

/*
 * B-9 (Daniel 2026-08-17): everything else loads on demand via React.lazy.
 * Each import() below becomes its own chunk (plus shared chunks Rollup
 * factors out), fetched the first time someone navigates to the route.
 * scripts/verify-routes.mjs still checks every route resolves to the same
 * component, and scripts/verify-bundle-budget.mjs pins the eager first-paint
 * JS under a byte budget so this cannot silently regrow.
 *
 * Every page module already had a default export (they were default imports
 * before), which is what React.lazy requires.
 */
const UnifiedInbox = lazy(() => import('./pages/Inbox'));
const Team = lazy(() => import('./pages/Team'));
const SettingsEmail = lazy(() => import('./pages/SettingsEmail'));
// Build 36: the settings directory that replaced eleven sidebar rows.
const SettingsIndex = lazy(() => import('./pages/Settings/Index'));
const WebsiteSettings = lazy(() => import('./pages/WebsiteSettings'));
const MarketingStudio = lazy(() => import('./pages/Marketing/Studio'));
const MarketingCreate = lazy(() => import('./pages/Marketing/Create'));
const MarketingCalendarPage = lazy(() => import('./pages/Marketing/Calendar'));
const MarketingBrand = lazy(() => import('./pages/Marketing/Brand'));
const MarketingGraphics = lazy(() => import('./pages/Marketing/Graphics'));
// IDX-3 (Daniel 2026-08-13): manage the public MLS search pages without code.
const MarketingSiteSearches = lazy(() => import('./pages/Marketing/SiteSearches'));
const Packets = lazy(() => import('./pages/Packets'));
const ConsultPackets = lazy(() => import('./pages/ConsultPackets'));
const MetricoolSettings = lazy(() => import('./pages/MetricoolSettings'));
const OnlineAuctionsList = lazy(() => import('./pages/OnlineAuctions/List'));
const OnlineAuctionDetail = lazy(() => import('./pages/OnlineAuctions/Detail'));
const AdminUsage = lazy(() => import('./pages/AdminUsage'));
const AdminErrors = lazy(() => import('./pages/AdminErrors'));
// Build 39: what anybody actually opens.
const AdminPageViews = lazy(() => import('./pages/AdminPageViews'));
const AdminAgents = lazy(() => import('./pages/AdminAgents'));
// Build 71: routing for every lead source.
const LeadRoutingSettings = lazy(() => import('./pages/Admin/LeadRouting'));
const BuyerAccounts = lazy(() => import('./pages/BuyerAccounts'));
const CallReview = lazy(() => import('./pages/CallReview'));
const QuoHealth = lazy(() => import('./pages/QuoHealth'));
const AdminAutomations = lazy(() => import('./pages/AdminAutomations'));
const AdPerformance = lazy(() => import('./pages/AdPerformance'));
// F-1 (Daniel 2026-08-04): owner-only live-bank-feed dashboard.
const FinancesPage = lazy(() => import('./pages/Finances'));
const AdminImportClosings = lazy(() => import('./pages/AdminImportClosings'));
const AdminDealOrigins = lazy(() => import('./pages/AdminDealOrigins'));
const CompLedger = lazy(() => import('./pages/CompLedger'));
const CoachingPayments = lazy(() => import('./pages/Coaching'));
const AdminExportTokens = lazy(() => import('./pages/AdminExportTokens'));
const AiConnections = lazy(() => import('./pages/AiConnections'));
const AiDrafts = lazy(() => import('./pages/AiDrafts'));
const OAuthAuthorize = lazy(() => import('./pages/OAuthAuthorize'));
const WeeklyDraftsPage = lazy(() => import('./pages/WeeklyDrafts'));
const SellerUpdatesPage = lazy(() => import('./pages/SellerUpdates'));
const TcTodayPage = lazy(() => import('./pages/TcToday'));
const OneOnOnesPage = lazy(() => import('./pages/OneOnOnes'));
const TwelveWeekYearPage = lazy(() => import('./pages/TwelveWeekYear'));
const BusinessPlanPage = lazy(() => import('./pages/BusinessPlan'));
const TeamHealthPage = lazy(() => import('./pages/TeamHealth'));
const ContactsDuplicates = lazy(() => import('./pages/Contacts/Duplicates'));
const ContactsTrash = lazy(() => import('./pages/Contacts/Trash'));
const NurturePage = lazy(() => import('./pages/Contacts/Nurture'));
const DealAnalyzerPage = lazy(() => import('./pages/DealAnalyzer/Analyzer'));
const TodayPage = lazy(() => import('./pages/Today'));
const TransactionsList = lazy(() => import('./pages/Transactions/List'));
const TransactionsBoard = lazy(() => import('./pages/Transactions/Board'));
const TransactionsTrash = lazy(() => import('./pages/Transactions/Trash'));
const NewTransaction = lazy(() => import('./pages/Transactions/New'));
const TransactionDetail = lazy(() => import('./pages/Transactions/Detail'));
const EditTransaction = lazy(() => import('./pages/Transactions/Edit'));
const ContactsList = lazy(() => import('./pages/Contacts/List'));
const NewContact = lazy(() => import('./pages/Contacts/New'));
const ContactDetail = lazy(() => import('./pages/Contacts/Detail'));
const EditContact = lazy(() => import('./pages/Contacts/Edit'));
const ContactAnniversaries = lazy(() => import('./pages/Contacts/Anniversaries'));
// Build 56 (Daniel 2026-09-23): equity and refi flags across the database.
const ContactEquity = lazy(() => import('./pages/Contacts/Equity'));
// Build 57 (Daniel 2026-09-23): merge contacts, choosing which details to keep.
const ContactMerge = lazy(() => import('./pages/Contacts/Merge'));
const ContactsDialer = lazy(() => import('./pages/Contacts/Dialer'));
const CallSheetsPage = lazy(() => import('./pages/Contacts/CallSheets'));
const CallSheetDetailPage = lazy(() => import('./pages/Contacts/CallSheetDetail'));
const ProductionOverview = lazy(() => import('./pages/Production/Overview'));
// Build 10 (Daniel 2026-09-08): reporting with a real date picker.
const ReportsPage = lazy(() => import('./pages/Reports/Index'));
const ReportDealsPage = lazy(() => import('./pages/Reports/Deals'));
const ReportPipelinePage = lazy(() => import('./pages/Reports/Pipeline'));
const BusinessHealthPage = lazy(() => import('./pages/Reports/Business'));
const BusinessBehindPage = lazy(() => import('./pages/Reports/BusinessBehind'));
const MonthCheckPage = lazy(() => import('./pages/Reports/MonthCheck'));
const AgentDetail = lazy(() => import('./pages/Production/AgentDetail'));
const CoachScorecard = lazy(() => import('./pages/Production/CoachScorecard'));
const LifeByDesign = lazy(() => import('./pages/Tools/LifeByDesign'));
const TranscribePage = lazy(() => import('./pages/Tools/Transcribe'));
const TemplatesList = lazy(() => import('./pages/Templates/List'));
const VendorsList = lazy(() => import('./pages/Vendors/List'));
const LenderPreApprovals = lazy(() => import('./pages/Lender/PreApprovals'));
const LenderPreApprovalDetail = lazy(() => import('./pages/Lender/PreApprovalDetail'));
const PlansList = lazy(() => import('./pages/Plans/List'));
const PlanEditor = lazy(() => import('./pages/Plans/Editor'));
const FormsList = lazy(() => import('./pages/Forms/List'));
const FormEditor = lazy(() => import('./pages/Forms/Editor'));
const PublicForm = lazy(() => import('./pages/Forms/Public'));
const ClientPortalPage = lazy(() => import('./pages/ClientPortal'));
const PublicOffer = lazy(() => import('./pages/PublicOffer'));
const ContentGenerationView = lazy(() => import('./pages/Content/Generation'));
const MetaIntegrationSettings = lazy(() => import('./pages/Integrations/Meta'));
// Build 70: portal lead emails.
const LeadEmailSettings = lazy(() => import('./pages/Integrations/LeadEmail'));
const Privacy = lazy(() => import('./pages/Privacy'));
const DocumentTemplatesList = lazy(() => import('./pages/DocumentTemplates/List'));
const DocumentTemplateMapper = lazy(() => import('./pages/DocumentTemplates/Mapper'));
const TeamTasksList = lazy(() => import('./pages/Tasks/List'));
const CalendarPage = lazy(() => import('./pages/Calendar/Calendar'));
const LeadsList = lazy(() => import('./pages/Leads/List'));
const FunnelDashboard = lazy(() => import('./pages/Funnel/Dashboard'));
const NewLead = lazy(() => import('./pages/Leads/New'));
// Build 11 (2026-09-14): phone-sized intake and the follow-ups board.
const QuickAddLead = lazy(() => import('./pages/Leads/Quick'));
const FollowUpsBoard = lazy(() => import('./pages/Leads/FollowUps'));
const LeadDetail = lazy(() => import('./pages/Leads/Detail'));
const SubmitIndex = lazy(() => import('./pages/Submit/Index'));
const SubmissionsInbox = lazy(() => import('./pages/Submit/Inbox'));
const SubmissionDetail = lazy(() => import('./pages/Submit/Detail'));
const ListingAgreementForm = lazy(() => import('./pages/Submit/ListingAgreement'));
const ListingContractForm = lazy(() => import('./pages/Submit/ListingContract'));
const BuyerDocsForm = lazy(() => import('./pages/Submit/BuyerDocs'));
const BuyerContractForm = lazy(() => import('./pages/Submit/BuyerContract'));
const BuyerOfferForm = lazy(() => import('./pages/Submit/BuyerOffer'));

/*
 * B-9 (Daniel 2026-08-17): what shows while a lazy chunk downloads.
 * Same muted "Loading" line the pages themselves use for data fetches, so
 * a chunk load and a data load look identical to the person waiting. No
 * hooks in here and none in App, so hook order cannot change between
 * renders (React error #310 blanks the whole page and TypeScript does not
 * catch it).
 */
function RouteLoading() {
  return (
    <div style={{ padding: 32, fontSize: 13, color: 'var(--text-muted)' }}>
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<RouteLoading />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Public — no auth required so locked-out users can reach them. */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public privacy policy — required by Meta App Review and linked
          publicly to leads / clients. No auth, no portal restrictions. */}
      <Route path="/privacy" element={<Privacy />} />
      {/* Public sign-up forms at /f/<slug>. No auth — anyone with the
          URL can submit. Mounted near the top of the route table so it
          never gets shadowed by an authenticated catch-all. */}
      <Route path="/f/:slug" element={<PublicForm />} />
      {/* L-101 (Daniel 2026-07-08): client portal — token-authed
          read-only deal status. No login, no layout chrome. */}
      <Route path="/portal/tx/:token" element={<ClientPortalPage />} />
      {/* Offer submission (migration 0145, 2026-07-26): a buyer's agent
          with NO TR account submits an offer through the tokenized link
          the listing agent shared. No auth, no layout chrome. Mounted up
          here so the authenticated catch-all never shadows it. */}
      <Route path="/offer/:token" element={<PublicOffer />} />
      <Route
        path="/change-password"
        element={
          <RequireAuth portals={['agent', 'partner', 'client']}>
            <ChangePassword />
          </RequireAuth>
        }
      />
      <Route
        path="/pre-approvals"
        element={
          <StaffToLead>
            <RequireAuth portals={['partner']}>
              <LenderPreApprovals />
            </RequireAuth>
          </StaffToLead>
        }
      />
      <Route
        path="/pre-approvals/:id"
        element={
          <StaffToLead>
            <RequireAuth portals={['partner']}>
              <LenderPreApprovalDetail />
            </RequireAuth>
          </StaffToLead>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth portals={['agent', 'partner', 'client']}>
            <Home />
          </RequireAuth>
        }
      />
      {/* M-10 (Daniel 2026-07-30): unified inbox — every inbound text,
          email reply, and missed call in one working list. Agent only. */}
      <Route
        path="/inbox"
        element={
          <RequireAuth portals={['agent']}>
            <UnifiedInbox />
          </RequireAuth>
        }
      />
      <Route
        path="/team"
        element={
          <RequireAuth portals={['agent']}>
            <Team />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth portals={['agent']}>
            <SettingsIndex />
          </RequireAuth>
        }
      />
      {/* Build 39: the page-view summary. Owner and operations; the API
          enforces the same pair with requireRole. */}
      <Route
        path="/admin/page-views"
        element={
          <RequireAuth portals={['agent']}>
            <AdminPageViews />
          </RequireAuth>
        }
      />
      <Route
        path="/settings/email"
        element={
          <RequireAuth portals={['agent']}>
            <SettingsEmail />
          </RequireAuth>
        }
      />
      <Route
        path="/settings/website"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><WebsiteSettings /></Layout>
          </RequireAuth>
        }
      />
      {/*
        ═══ Marketing: five surfaces ═══════════════════════════════════
        Step 5. Eleven top-level links plus four unreachable pages became
        Studio / Create / Calendar / Brand / Graphics. Every old path below
        still resolves, as a redirect — see the redirect block further down.
      */}
      <Route
        path="/marketing"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><MarketingStudio /></Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/create"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><MarketingCreate /></Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/calendar"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><MarketingCalendarPage /></Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/brand"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><MarketingBrand /></Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/graphics"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><MarketingGraphics /></Layout>
          </RequireAuth>
        }
      />
      {/* IDX-3 (Daniel 2026-08-13): sixth marketing surface. The saved
          searches that ARE the public /search pages on turnkeywichita.com,
          plus MLS feed health and the leads the site produced. */}
      <Route
        path="/marketing/site-searches"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><MarketingSiteSearches /></Layout>
          </RequireAuth>
        }
      />

      {/*
        ═══ Old marketing paths ════════════════════════════════════════
        Redirects, not deletions. Daniel's team has these bookmarked and in
        muscle memory; a 404 on a link someone used yesterday is a support
        request and a reason to distrust the next change. `replace` so Back
        does not bounce them into the redirect again.

        These can be removed once nobody is hitting them, which is a
        different decision made with data rather than now.
      */}
      <Route path="/marketing-assistant" element={<Navigate to="/marketing" replace />} />
      <Route path="/marketing/pillars" element={<Navigate to="/marketing/brand?t=pillars" replace />} />
      <Route path="/marketing/email-blasts" element={<Navigate to="/marketing/create?t=blasts" replace />} />
      <Route path="/marketing/newsletter" element={<Navigate to="/marketing/create?t=newsletter" replace />} />
      <Route path="/marketing/blog" element={<Navigate to="/marketing/create?t=blog" replace />} />
      <Route path="/marketing/listing-graphics" element={<Navigate to="/marketing/graphics?t=make" replace />} />
      <Route path="/marketing/social-graphics" element={<Navigate to="/marketing/graphics?t=slides" replace />} />
      {/* Review graphics was deleted in step 6. The canvas renders that card
          better, so its old path lands on the canvas rather than 404ing. */}
      <Route path="/marketing/review-graphics" element={<Navigate to="/marketing/graphics?t=make" replace />} />
      <Route path="/marketing/social-publishing" element={<Navigate to="/settings/social-publishing" replace />} />
      <Route path="/content" element={<Navigate to="/marketing/brand?t=avatars" replace />} />
      <Route path="/content/voice-profile" element={<Navigate to="/marketing/brand?t=my-voice" replace />} />
      <Route path="/content/company-voice" element={<Navigate to="/marketing/brand?t=turnkey-voice" replace />} />

      {/* Consult guides and document packets moved to Tools. */}
      <Route path="/marketing/consult-packets" element={<Navigate to="/tools/consult-guides" replace />} />
      <Route path="/marketing/packets" element={<Navigate to="/tools/document-packets" replace />} />
      <Route
        path="/tools/consult-guides"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><ConsultPackets /></Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/tools/document-packets"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><Packets /></Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/social-publishing"
        element={
          <RequireAuth portals={['agent']}>
            <Layout><MetricoolSettings /></Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/online-auctions"
        element={
          <RequireAuth portals={['agent']}>
            <OnlineAuctionsList />
          </RequireAuth>
        }
      />
      <Route
        path="/online-auctions/:id"
        element={
          <RequireAuth portals={['agent']}>
            <OnlineAuctionDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/errors"
        element={
          <RequireAuth portals={['agent']}>
            <AdminErrors />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/usage"
        element={
          <RequireAuth portals={['agent']}>
            <AdminUsage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/agents"
        element={
          <RequireAuth portals={['agent']}>
            <AdminAgents />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/lead-routing"
        element={
          <RequireAuth portals={['agent']}>
            <LeadRoutingSettings />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/buyer-accounts"
        element={
          <RequireAuth portals={['agent']}>
            <BuyerAccounts />
          </RequireAuth>
        }
      />
      <Route
        path="/call-review"
        element={
          <RequireAuth portals={['agent']}>
            <CallReview />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/quo-health"
        element={
          <RequireAuth portals={['agent']}>
            <QuoHealth />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/automations"
        element={
          <RequireAuth portals={['agent']}>
            <AdminAutomations />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/import-closings"
        element={
          <RequireAuth portals={['agent']}>
            <AdminImportClosings />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/deal-origins"
        element={
          <RequireAuth portals={['agent']}>
            <AdminDealOrigins />
          </RequireAuth>
        }
      />
      <Route
        path="/comp-ledger"
        element={
          <RequireAuth portals={['agent']}>
            <CompLedger />
          </RequireAuth>
        }
      />
      <Route
        path="/ai-drafts"
        element={
          <RequireAuth portals={['agent']}>
            <AiDrafts />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/ai-connections"
        element={
          <RequireAuth portals={['agent']}>
            <AiConnections />
          </RequireAuth>
        }
      />
      <Route
        path="/oauth/authorize"
        element={
          <RequireAuth portals={['agent']}>
            <OAuthAuthorize />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/export-tokens"
        element={
          <RequireAuth portals={['agent']}>
            <AdminExportTokens />
          </RequireAuth>
        }
      />
      <Route
        path="/weekly-drafts"
        element={
          <RequireAuth portals={['agent']}>
            <WeeklyDraftsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/seller-updates"
        element={
          <RequireAuth portals={['agent']}>
            <SellerUpdatesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/tc-today"
        element={
          <RequireAuth portals={['agent']}>
            <TcTodayPage />
          </RequireAuth>
        }
      />
      <Route
        path="/coaching"
        element={
          <RequireAuth portals={['agent']}>
            <CoachingPayments />
          </RequireAuth>
        }
      />
      <Route
        path="/one-on-ones"
        element={
          <RequireAuth portals={['agent']}>
            <OneOnOnesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/12-week-year"
        element={
          <RequireAuth portals={['agent']}>
            <TwelveWeekYearPage />
          </RequireAuth>
        }
      />
      <Route
        path="/business-plan"
        element={
          <RequireAuth portals={['agent']}>
            <BusinessPlanPage />
          </RequireAuth>
        }
      />
      {/* Build 36: the recruiting front door. Same page, opened on a recruit
          packet instead of on your own plan. A separate path rather than a
          query string, so the sidebar can highlight one row and not two. */}
      <Route
        path="/recruits"
        element={
          <RequireAuth portals={['agent']}>
            <BusinessPlanPage />
          </RequireAuth>
        }
      />
      <Route
        path="/ad-performance"
        element={
          <RequireAuth portals={['agent']}>
            <AdPerformance />
          </RequireAuth>
        }
      />
      {/* F-1 (Daniel 2026-08-04): the finance dashboard. Agent portal only
          here; the API additionally requires owner/operations on every
          endpoint, so another role landing on the URL sees only 403s. The
          nav link itself is role-gated in Layout.tsx. */}
      <Route
        path="/finances"
        element={
          <RequireAuth portals={['agent']}>
            <FinancesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/production/health"
        element={
          <RequireAuth portals={['agent']}>
            <TeamHealthPage />
          </RequireAuth>
        }
      />
      <Route
        path="/contacts/duplicates"
        element={
          <RequireAuth portals={['agent']}>
            <ContactsDuplicates />
          </RequireAuth>
        }
      />
      {/* Build 61: the Trash the delete dialogs promise. Above :id. */}
      <Route
        path="/contacts/trash"
        element={
          <RequireAuth portals={['agent']}>
            <ContactsTrash />
          </RequireAuth>
        }
      />
      <Route
        path="/contacts/nurture"
        element={
          <RequireAuth portals={['agent']}>
            <NurturePage />
          </RequireAuth>
        }
      />
      <Route
        path="/investor-analyzer"
        element={
          <RequireAuth portals={['agent']}>
            <DealAnalyzerPage />
          </RequireAuth>
        }
      />
      <Route
        path="/investor-analyzer/:id"
        element={
          <RequireAuth portals={['agent']}>
            <DealAnalyzerPage />
          </RequireAuth>
        }
      />
      {/* Build 64: the ranked daily list. */}
      <Route
        path="/today"
        element={
          <RequireAuth portals={['agent']}>
            <TodayPage />
          </RequireAuth>
        }
      />
      <Route
        path="/transactions"
        element={
          <RequireAuth portals={['agent', 'partner', 'client']}>
            <TransactionsList />
          </RequireAuth>
        }
      />
      <Route
        path="/transactions/board"
        element={
          <RequireAuth portals={['agent']}>
            <TransactionsBoard />
          </RequireAuth>
        }
      />
      {/* Build 62: the Trash the delete dialog promises. Above :id. */}
      <Route
        path="/transactions/trash"
        element={
          <RequireAuth portals={['agent']}>
            <TransactionsTrash />
          </RequireAuth>
        }
      />
      <Route
        path="/transactions/new"
        element={
          <RequireAuth>
            <NewTransaction />
          </RequireAuth>
        }
      />
      <Route
        path="/transactions/:id"
        element={
          <RequireAuth portals={['agent', 'partner', 'client']}>
            <TransactionDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/transactions/:id/edit"
        element={
          <RequireAuth>
            <EditTransaction />
          </RequireAuth>
        }
      />
      <Route
        path="/contacts"
        element={
          <RequireAuth>
            <ContactsList />
          </RequireAuth>
        }
      />
      {/* L-22 (2026-06-12): home-purchase anniversary list. Mounted
          above /:id so the literal segment doesn't get treated as a
          contact ID. */}
      <Route
        path="/contacts/anniversaries"
        element={
          <RequireAuth>
            <ContactAnniversaries />
          </RequireAuth>
        }
      />
      {/* Build 57: merge, choosing details. Above :id for the same reason. */}
      <Route
        path="/contacts/merge"
        element={
          <RequireAuth>
            <ContactMerge />
          </RequireAuth>
        }
      />
      {/* Build 56: equity and refi. Above :id for the same reason. */}
      <Route
        path="/contacts/equity"
        element={
          <RequireAuth>
            <ContactEquity />
          </RequireAuth>
        }
      />
      {/* M-12 (Daniel 2026-07-30): Power Dialer calling session. Mounted
          above :id so the literal segment doesn't get treated as a
          contact ID. */}
      <Route
        path="/contacts/dialer"
        element={
          <RequireAuth portals={['agent']}>
            <ContactsDialer />
          </RequireAuth>
        }
      />
      {/* R7-6 (Daniel 2026-08-05): saved call sheets. Both above :id for
          the same reason the dialer is. */}
      <Route
        path="/contacts/call-sheets"
        element={
          <RequireAuth portals={['agent']}>
            <CallSheetsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/contacts/call-sheets/:id"
        element={
          <RequireAuth portals={['agent']}>
            <CallSheetDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/contacts/new"
        element={
          <RequireAuth>
            <NewContact />
          </RequireAuth>
        }
      />
      <Route
        path="/contacts/:id"
        element={
          <RequireAuth>
            <ContactDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/contacts/:id/edit"
        element={
          <RequireAuth>
            <EditContact />
          </RequireAuth>
        }
      />
      <Route
        path="/reports"
        element={
          <RequireAuth portals={['agent']}>
            <ReportsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/reports/pipeline"
        element={
          <RequireAuth portals={['agent']}>
            <ReportPipelinePage />
          </RequireAuth>
        }
      />
      <Route
        path="/reports/deals"
        element={
          <RequireAuth portals={['agent']}>
            <ReportDealsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/reports/business"
        element={
          <RequireAuth portals={['agent']}>
            <BusinessHealthPage />
          </RequireAuth>
        }
      />
      <Route
        path="/reports/business/behind"
        element={
          <RequireAuth portals={['agent']}>
            <BusinessBehindPage />
          </RequireAuth>
        }
      />
      <Route
        path="/reports/month"
        element={
          <RequireAuth portals={['agent']}>
            <MonthCheckPage />
          </RequireAuth>
        }
      />
      <Route
        path="/production"
        element={
          <RequireAuth>
            <ProductionOverview />
          </RequireAuth>
        }
      />
      <Route
        path="/production/agents/:id"
        element={
          <RequireAuth>
            <AgentDetail />
          </RequireAuth>
        }
      />
      {/* R11-4 (Daniel 2026-08-12): the coach scorecard, team and per
          agent, printed monthly and quarterly for Daniel's coach. */}
      <Route
        path="/production/scorecard"
        element={
          <RequireAuth>
            <CoachScorecard />
          </RequireAuth>
        }
      />
      {/* Build 47 (Daniel 2026-09-21): the Income Projector and its
          bookmark redirect are gone. Its economic model is the packet's
          economic model under KW's field names, so the packet below is
          where this work happens now. The redirect went with the page
          rather than being left pointing at a route that no longer
          resolves. */}
      {/* Life by Design (Daniel 2026-08-22): the KW expense worksheet,
          savable per recruit. */}
      <Route
        path="/tools/life-by-design"
        element={
          <RequireAuth portals={['agent']}>
            <LifeByDesign />
          </RequireAuth>
        }
      />
      {/* M-14 (Daniel 2026-07-30): drop-a-recording transcriber. */}
      <Route
        path="/tools/transcribe"
        element={
          <RequireAuth portals={['agent']}>
            <TranscribePage />
          </RequireAuth>
        }
      />
      <Route
        path="/templates"
        element={
          <RequireAuth portals={['agent']}>
            <TemplatesList />
          </RequireAuth>
        }
      />
      <Route
        path="/vendors"
        element={
          <RequireAuth portals={['agent']}>
            <VendorsList />
          </RequireAuth>
        }
      />
      {/* L-63b (Daniel 2026-06-30) — /partners is a prefiltered variant of
          the vendors page showing only rows with has_portal_access=1
          (lenders + other partners). Admin-only role check lives inside
          the component. */}
      <Route
        path="/partners"
        element={
          <RequireAuth portals={['agent']}>
            <VendorsList mode="partners" />
          </RequireAuth>
        }
      />
      <Route
        path="/plans"
        element={
          <RequireAuth portals={['agent']}>
            <PlansList />
          </RequireAuth>
        }
      />
      <Route
        path="/plans/:id"
        element={
          <RequireAuth portals={['agent']}>
            <PlanEditor />
          </RequireAuth>
        }
      />
      <Route
        path="/forms"
        element={
          <RequireAuth portals={['agent']}>
            <FormsList />
          </RequireAuth>
        }
      />
      <Route
        path="/forms/:id"
        element={
          <RequireAuth portals={['agent']}>
            {/* Wrap in ErrorBoundary so a crash in the meta/event editor
                shows a readable error instead of white-screening — Daniel
                hit a blank-screen bug on the event date picker and the
                error message was invisible. */}
            <ErrorBoundary label="form editor">
              <FormEditor />
            </ErrorBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="/content/generations/:id"
        element={
          <RequireAuth portals={['agent']}>
            <ErrorBoundary label="content generation">
              <ContentGenerationView />
            </ErrorBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="/integrations/meta"
        element={
          <RequireAuth portals={['agent']}>
            <MetaIntegrationSettings />
          </RequireAuth>
        }
      />
      <Route
        path="/integrations/lead-email"
        element={
          <RequireAuth portals={['agent']}>
            <LeadEmailSettings />
          </RequireAuth>
        }
      />
      <Route
        path="/document-templates"
        element={
          <RequireAuth portals={['agent']}>
            <DocumentTemplatesList />
          </RequireAuth>
        }
      />
      <Route
        path="/document-templates/:id/map"
        element={
          <RequireAuth portals={['agent']}>
            {/* The mapper does heavy DOM work (pdf.js worker, dynamic
                canvas insertion, 40+ overlay buttons) — a malformed PDF
                or pdf.js worker failure used to white-screen the whole
                app. Catch those locally and show a recovery view. */}
            <ErrorBoundary label="the document mapper">
              <DocumentTemplateMapper />
            </ErrorBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="/tasks"
        element={
          <RequireAuth>
            <TeamTasksList />
          </RequireAuth>
        }
      />
      <Route
        path="/calendar"
        element={
          <RequireAuth>
            <CalendarPage />
          </RequireAuth>
        }
      />
      <Route
        path="/leads"
        element={
          <RequireAuth>
            <LeadsList />
          </RequireAuth>
        }
      />
      <Route
        path="/leads/new"
        element={
          <RequireAuth>
            <NewLead />
          </RequireAuth>
        }
      />
      <Route
        path="/leads/quick"
        element={
          <RequireAuth portals={['agent']}>
            <QuickAddLead />
          </RequireAuth>
        }
      />
      <Route
        path="/leads/follow-ups"
        element={
          <RequireAuth portals={['agent']}>
            <FollowUpsBoard />
          </RequireAuth>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <RequireAuth>
            <LeadDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/funnel"
        element={
          <RequireAuth>
            <FunnelDashboard />
          </RequireAuth>
        }
      />
      <Route path="/submit" element={<RequireAuth><SubmitIndex /></RequireAuth>} />
      <Route path="/submit/inbox" element={<RequireAuth><SubmissionsInbox /></RequireAuth>} />
      <Route path="/submit/listing-agreement" element={<RequireAuth><ListingAgreementForm /></RequireAuth>} />
      <Route path="/submit/listing-contract" element={<RequireAuth><ListingContractForm /></RequireAuth>} />
      <Route path="/submit/buyer-docs" element={<RequireAuth><BuyerDocsForm /></RequireAuth>} />
      <Route path="/submit/buyer-contract" element={<RequireAuth><BuyerContractForm /></RequireAuth>} />
      {/* L-103 (Daniel 2026-07-08): submit a NEW offer for a buyer. */}
      <Route path="/submit/buyer-offer" element={<RequireAuth><BuyerOfferForm /></RequireAuth>} />
      <Route path="/submit/:id" element={<RequireAuth><SubmissionDetail /></RequireAuth>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}
